import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Print as PrintIcon, Email as EmailIcon } from '@mui/icons-material';
import PageLayout from '../components/common/PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { get, post } from '../utils/api';

// Absolute API base — matches api.js so the print window fetch hits the backend origin (dev :5001).
function apiUrl(path) {
  let base = process.env.REACT_APP_API_BASE_URL || '';
  if (!base) {
    try {
      const loc = window.location;
      if (/localhost|127\.0\.0\.1/.test(loc.hostname)) base = 'http://localhost:5001';
    } catch (_) { /* noop */ }
  }
  return base.replace(/\/$/, '') + path;
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function fmtDate(d) {
  // d is 'YYYY-MM-DD'; render as "Sat, Jan 24" without timezone drift.
  const [y, m, day] = String(d).split('-').map(Number);
  if (!y) return d;
  const dt = new Date(Date.UTC(y, m - 1, day));
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1];
  return `${WEEKDAY[dt.getUTCDay()]}, ${mon} ${day}`;
}

export default function ManageRostersPage() {
  const currentYear = new Date().getFullYear();
  const { staff } = useAuth();
  const [years, setYears] = React.useState([currentYear]);
  const [year, setYear] = React.useState(currentYear);
  const [tab, setTab] = React.useState('trustees');
  const [trustees, setTrustees] = React.useState({ trustees: [], total: 0, assignable: 0, exempted: 0 });
  const [tod, setTod] = React.useState({ roster: [], total: 0 });
  const [eco, setEco] = React.useState({ roster: [], total: 0 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  // Email dialog
  const [emailOpen, setEmailOpen] = React.useState(false);
  const [emailTo, setEmailTo] = React.useState('');
  const [emailBusy, setEmailBusy] = React.useState(false);
  const [emailMsg, setEmailMsg] = React.useState(null); // { type, text }

  const rosterLabel = tab === 'tod' ? 'Trustee on Duty' : (tab === 'eco' ? 'Event Coordinators' : 'Trustees');

  const openPrint = async () => {
    const w = window.open('', '_blank');
    try {
      const res = await fetch(apiUrl(`/api/manage/rosters/print?type=${tab}&year=${year}`), {
        credentials: 'include', headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) throw new Error('Failed to open print view.');
      const html = await res.text();
      if (w) { w.document.open(); w.document.write(html); w.document.close(); }
    } catch (e) {
      if (w) w.close();
      setError(e?.message || 'Failed to open print view.');
    }
  };

  const openEmail = () => {
    setEmailTo(staff?.email || '');
    setEmailMsg(null);
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    setEmailBusy(true);
    setEmailMsg(null);
    try {
      const res = await post('/api/manage/rosters/email', { type: tab, year, to: emailTo });
      setEmailMsg({ type: 'success', text: `Sent to ${(res?.to || []).length} recipient(s).` });
    } catch (e) {
      setEmailMsg({ type: 'error', text: e?.message || 'Failed to send email.' });
    } finally {
      setEmailBusy(false);
    }
  };

  React.useEffect(() => {
    (async () => {
      try {
        const res = await get('/api/manage/rosters/years');
        const ys = Array.isArray(res?.years) && res.years.length ? res.years : [currentYear];
        setYears(ys);
        setYear(ys.includes(currentYear) ? currentYear : ys[0]);
      } catch (_) { /* keep default */ }
    })();
  }, [currentYear]);

  const load = React.useCallback(async (yr) => {
    setLoading(true);
    setError('');
    try {
      const [t, d, e] = await Promise.all([
        get('/api/manage/rosters/trustees', { year: yr }),
        get('/api/manage/rosters/trustee-on-duty', { year: yr }),
        get('/api/manage/rosters/event-coordinators', { year: yr }),
      ]);
      setTrustees({
        trustees: t?.trustees || [], total: t?.total || 0,
        assignable: t?.assignable || 0, exempted: t?.exempted || 0,
      });
      setTod({ roster: d?.roster || [], total: d?.total || 0 });
      setEco({ roster: e?.roster || [], total: e?.total || 0 });
    } catch (e) {
      setError(e?.message || 'Failed to load rosters.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(year); }, [year, load]);

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Rosters</Typography>
          <Typography variant="body2" color="text.secondary">
            Trustees, Trustee-on-Duty, and Event Coordinator assignments. Names are the formal trustee names.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={openPrint} disabled={loading}>
            Print / PDF
          </Button>
          <Button variant="outlined" startIcon={<EmailIcon />} onClick={openEmail} disabled={loading}>
            Email
          </Button>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel id="roster-year-label">Year</InputLabel>
            <Select
              labelId="roster-year-label"
              label="Year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <Dialog open={emailOpen} onClose={() => !emailBusy && setEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Email {rosterLabel} roster — {year}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sends the {rosterLabel} roster for {year} as a formatted email. Separate multiple addresses with commas.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Recipient email(s)"
            placeholder="name@example.com, another@example.com"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            disabled={emailBusy}
          />
          {emailMsg && (
            <Alert severity={emailMsg.type} sx={{ mt: 2 }}>{emailMsg.text}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOpen(false)} disabled={emailBusy}>Close</Button>
          <Button variant="contained" onClick={sendEmail} disabled={emailBusy || !emailTo.trim()}>
            {emailBusy ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="trustees" label={`Trustees${trustees.total ? ` (${trustees.total})` : ''}`} />
        <Tab value="tod" label={`Trustee on Duty${tod.total ? ` (${tod.total})` : ''}`} />
        <Tab value="eco" label={`Event Coordinators${eco.total ? ` (${eco.total})` : ''}`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {tab === 'trustees' && (
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`${trustees.total} total`} />
                  <Chip color="success" variant="outlined" label={`${trustees.assignable} assignable`} />
                  <Chip color="default" variant="outlined" label={`${trustees.exempted} exempted`} />
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 48 }}>#</TableCell>
                      <TableCell>Full name</TableCell>
                      <TableCell align="right">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trustees.trustees.length === 0 && (
                      <TableRow><TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">No trustees loaded for {year}.</Typography>
                      </TableCell></TableRow>
                    )}
                    {trustees.trustees.map((t) => (
                      <TableRow key={`${t.seq}-${t.full_name}`} sx={t.is_exempted ? { opacity: 0.6 } : undefined}>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                          {String(t.seq ?? '').padStart(2, '0')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t.full_name}</TableCell>
                        <TableCell align="right">
                          {t.is_exempted
                            ? <Chip size="small" label="Exempted" />
                            : <Chip size="small" color="success" variant="outlined" label="Assignable" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {tab === 'tod' && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Saturday front-desk duty, two trustees per week. {tod.total} weeks in the {year} cycle.
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 140 }}>Date</TableCell>
                      <TableCell>Trustees on duty</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tod.roster.length === 0 && (
                      <TableRow><TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">No duty roster loaded for {year}.</Typography>
                      </TableCell></TableRow>
                    )}
                    {tod.roster.map((r) => (
                      <TableRow key={r.duty_date}>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDate(r.duty_date)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {(r.trustees || []).map((n, i) => (
                              <Chip key={`${r.duty_date}-${i}`} size="small" variant="outlined" label={n} />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{r.note || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {tab === 'eco' && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Coordinators assigned to each event, keyed to the calendar (the calendar is the source of truth for dates). {eco.total} events staffed in {year}.
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 140 }}>Date</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Coordinators</TableCell>
                      <TableCell sx={{ width: 120 }}>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eco.roster.length === 0 && (
                      <TableRow><TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">No coordinator assignments loaded for {year}.</Typography>
                      </TableCell></TableRow>
                    )}
                    {eco.roster.map((r) => (
                      <TableRow key={r.event_key}>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.event_date ? fmtDate(r.event_date) : '—'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{r.title || r.event_key}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {(r.coordinators || []).map((n, i) => (
                              <Chip key={`${r.event_key}-${i}`} size="small" variant="outlined" label={n} />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{r.note || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </PageLayout>
  );
}
